import { type ReactNode, useMemo, useState } from "react";
import { cx } from "./cx.js";
import { Icon } from "./Icon.jsx";

export interface Column<T> {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  /** Present = the column sorts, by this value. */
  sortValue?: (row: T) => string | number;
  align?: "left" | "right";
  /** Tabular mono figures for tags, values and hashes. */
  mono?: boolean;
  className?: string;
}

type Dir = "ascending" | "descending";

function compare(a: string | number, b: string | number): number {
  return typeof a === "number" && typeof b === "number"
    ? a - b
    : String(a).localeCompare(String(b), undefined, { numeric: true });
}

function useSorted<T>(rows: T[], columns: Column<T>[], initial?: { key: string; dir: Dir }) {
  const [sort, setSort] = useState(initial ?? null);
  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sort?.key);
    if (!sort || !col?.sortValue) return rows;
    const get = col.sortValue;
    const sign = sort.dir === "ascending" ? 1 : -1;
    return [...rows].sort((a, b) => sign * compare(get(a), get(b)));
  }, [rows, columns, sort]);
  const toggle = (key: string) =>
    setSort((s) => ({ key, dir: s?.key === key && s.dir === "ascending" ? "descending" : "ascending" }));
  return { sort, sorted, toggle };
}

/**
 * The one table. Sticky header, sortable columns (aria-sort on the header, a real button inside it), 40px rows with a
 * hover fill, and the three non-data states every list needs: loading, error (with the way to retry) and empty.
 */
export function DataTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  initialSort,
  loading,
  error,
  empty = "Nothing to show.",
  onRowClick,
  selectedKey,
  maxHeight,
  rowClassName,
}: {
  /** Names the table for assistive tech; shown to nobody else (the page header already titles it). */
  caption: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  initialSort?: { key: string; dir: Dir };
  loading?: boolean;
  error?: ReactNode;
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  selectedKey?: string | null;
  maxHeight?: string;
  rowClassName?: (row: T) => string | undefined;
}) {
  const { sort, sorted, toggle } = useSorted(rows, columns, initialSort);
  const note = (node: ReactNode, role: "status" | "alert") => (
    <tr>
      <td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-ink-muted" role={role}>
        {node}
      </td>
    </tr>
  );
  const body = loading
    ? note("Loading…", "status")
    : error
      ? note(error, "alert")
      : sorted.length === 0
        ? note(empty, "status")
        : sorted.map((row) => (
            <BodyRow
              key={rowKey(row)}
              row={row}
              columns={columns}
              selected={selectedKey === undefined ? undefined : selectedKey === rowKey(row)}
              {...(onRowClick ? { onClick: onRowClick } : {})}
              extra={rowClassName?.(row)}
            />
          ));
  return (
    <div className="overflow-auto rounded-lg border border-line bg-surface shadow-card" style={{ maxHeight }}>
      <table className="w-full border-collapse text-[13px] text-ink">
        <caption className="sr-only">{caption}</caption>
        <thead className="sticky top-0 z-10 bg-subtle">
          <tr>
            {columns.map((c) => (
              <HeadCell
                key={c.key}
                column={c}
                dir={sort?.key === c.key ? sort.dir : undefined}
                onSort={toggle}
              />
            ))}
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
    </div>
  );
}

function HeadCell<T>({
  column: c,
  dir,
  onSort,
}: {
  column: Column<T>;
  dir: Dir | undefined;
  onSort: (key: string) => void;
}) {
  const icon = dir === "descending" ? "arrow_downward" : dir ? "arrow_upward" : "unfold_more";
  return (
    <th
      scope="col"
      aria-sort={c.sortValue ? (dir ?? "none") : undefined}
      className={cx(
        "h-10 border-b border-line px-3 text-xs font-semibold text-ink-muted",
        c.align === "right" ? "text-right" : "text-left",
      )}
    >
      {c.sortValue ? (
        <button
          type="button"
          onClick={() => onSort(c.key)}
          className="inline-flex items-center gap-1 rounded bg-transparent p-0 font-semibold text-inherit hover:text-ink"
        >
          {c.header}
          <Icon name={icon} size={14} />
        </button>
      ) : (
        c.header
      )}
    </th>
  );
}

function BodyRow<T>({
  row,
  columns,
  selected,
  onClick,
  extra,
}: {
  row: T;
  columns: Column<T>[];
  selected: boolean | undefined;
  onClick?: (row: T) => void;
  extra: string | undefined;
}) {
  return (
    <tr
      aria-selected={selected}
      onClick={onClick ? () => onClick(row) : undefined}
      className={cx(
        "h-10 border-b border-line last:border-b-0 hover:bg-subtle",
        onClick && "cursor-pointer",
        selected && "bg-brand-subtle",
        extra,
      )}
    >
      {columns.map((c) => (
        <td
          key={c.key}
          className={cx(
            "px-3 py-1.5 align-middle",
            c.align === "right" && "text-right",
            c.mono && "font-mono tabular-nums",
            c.className,
          )}
        >
          {c.cell(row)}
        </td>
      ))}
    </tr>
  );
}
